import { useEffect, useRef, useState } from "react";

export default function SeatmapInteractionGuard({
    children,
    className = "",
    overlayClassName = "",
    desktopMessage = "A térkép a ctrl + görgetés használatával nagyítható",
    mobileMessage = "Két ujjal mozgassa a térképet",
}) {
    const boundRef = useRef(false);
    const [showHint, setShowHint] = useState(false);

    // mobil drag detekt
    const touchStartRef = useRef({ x: 0, y: 0, dragging: false });

    // desktop timer
    const hideTimerRef = useRef(null);

    const isDesktop = () => {
        if (typeof window === "undefined") return true;
        return window.matchMedia?.("(pointer: fine)").matches;
    };

    const currentMessage = isDesktop() ? desktopMessage : mobileMessage;

    const showHintMaybeTimed = () => {
        setShowHint(true);

        // ✅ csak gépen: 1mp múlva tűnjön el
        if (isDesktop()) {
            if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
            hideTimerRef.current = setTimeout(() => {
                setShowHint(false);
            }, 1000);
        }
    };

    const hideHintNow = () => {
        if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
        setShowHint(false);
    };

    useEffect(() => {
        if (boundRef.current) return;
        boundRef.current = true;

        const onKeyDown = (e) => {
            if (e.key === "Control" || e.ctrlKey) hideHintNow();
        };

        window.addEventListener("keydown", onKeyDown);
    }, []);

    // Desktop: Ctrl nélküli wheel -> show + timed hide, Ctrl+wheel -> hide
    const onWheel = (e) => {
        if (e.ctrlKey) {
            hideHintNow();
            return;
        }
        showHintMaybeTimed();
    };

    // kattintásra tűnjön el (nem nyel el semmit)
    const onPointerDownCapture = () => {
        hideHintNow();
    };

    // Mobil: csak 1 ujjas HÚZÁSRA jöjjön be (tapre ne)
    const onTouchStart = (e) => {
        const touches = e.touches?.length ?? 0;

        if (touches >= 2) {
            hideHintNow();
            touchStartRef.current.dragging = false;
            return;
        }

        if (touches === 1) {
            const t = e.touches[0];
            touchStartRef.current = { x: t.clientX, y: t.clientY, dragging: false };
        }
    };

    const onTouchMove = (e) => {
        const touches = e.touches?.length ?? 0;

        if (touches >= 2) {
            hideHintNow();
            touchStartRef.current.dragging = false;
            return;
        }

        if (touches === 1) {
            const t = e.touches[0];
            const dx = t.clientX - touchStartRef.current.x;
            const dy = t.clientY - touchStartRef.current.y;

            const THRESHOLD = 8;
            if (!touchStartRef.current.dragging && (Math.abs(dx) > THRESHOLD || Math.abs(dy) > THRESHOLD)) {
                touchStartRef.current.dragging = true;
                setShowHint(true); // mobilon nincs timer
            }
        }
    };

    const onTouchEnd = () => {
        touchStartRef.current.dragging = false;
        hideHintNow();
    };

    const onTouchCancel = () => {
        touchStartRef.current.dragging = false;
        hideHintNow();
    };

    return (
        <div
            className={`relative w-full h-full ${className}`}
            onWheel={onWheel}
            onPointerDownCapture={onPointerDownCapture}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            onTouchCancel={onTouchCancel}
        >
            {showHint && (
                <div
                    className={`pointer-events-none select-none absolute inset-0 z-50 flex items-center justify-center ${overlayClassName}`}
                >
                    <div className="absolute inset-0 bg-black/35 backdrop-blur-[2px]" />
                    <div className="relative px-6 text-white font-semibold text-lg md:text-xl tracking-wide drop-shadow-[0_2px_10px_rgba(0,0,0,0.65)]">
                        {currentMessage}
                    </div>
                </div>
            )}

            {children}
        </div>
    );
}
